import React, { useEffect, useMemo, useState } from "react";
import { QueryKey } from "react-query";
import { useTranslation } from "react-i18next";

import { EditIcon } from "fe-shared/src/components/Icons";
import {
  PermissionLevel,
  TireMaterialConfig,
  UserPermission,
} from "fe-shared/src/types/domain";
import {
  Transaction,
  TransactionSection,
  TransactionSubSection,
  TransactionFlag,
} from "fe-shared-transactions/src/types/details";
import buildProcessorWarehouse from "fe-shared-transactions/src/helpers/buildProcessorWarehouse";
import {
  DetailsLocationsSection,
  DetailsParticipantsSection,
  DetailsDataSection,
  CorrectionsDetails,
} from "fe-shared-transactions/src/components";
import { Banner, IconButton } from "fe-shared/src/components";
import {
  Corrections,
  TransactionStatus,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { PERMISSIONS } from "consts";
import FlagComments from "../FlagComments";
import SectionAction from "../SectionAction";
import { actions, isTransactionLoadChanged } from "./modules/config";
import styles from "./TransactionContentView.module.scss";

interface Props {
  transaction: Transaction;
  linkedOriginal?: Transaction;
  linkedAdjustment?: Transaction;
  corrections: Corrections | null;
  activitiesQueryKey: QueryKey;
  transactionQueryKey: QueryKey;
  correctionsQueryKey: QueryKey;
  isCurrentUserAssignee: boolean;
  isPermitted: (permission: UserPermission) => boolean;
  canFlagManually?: boolean;
  canEditManualFlag?: boolean;
  canUnflag?: boolean;
  canOverrideRegYear?: boolean;
  canEditRetreadingTransaction?: boolean;
  dataSectionCallback?: () => void;
  materials?: TireMaterialConfig[];
  isInitiatedDelivery?: boolean;
  targetCompanyInfo?: Transaction['targetCompanyInfo'];
}

const { Locations, Participants, Data } = TransactionSection;
const { CommonData } = TransactionSubSection;

const TransactionContentView = ({
  transaction,
  linkedOriginal,
  linkedAdjustment,
  corrections,
  activitiesQueryKey,
  transactionQueryKey,
  correctionsQueryKey,
  isCurrentUserAssignee,
  isPermitted,
  canFlagManually,
  canEditManualFlag,
  canUnflag,
  canOverrideRegYear,
  canEditRetreadingTransaction,
  dataSectionCallback,
  materials,
  isInitiatedDelivery,
  targetCompanyInfo,
}: Props) => {
  const {
    sourceWarehouse,
    targetWarehouse,
    transactionFlags,
    transactionId,
    transactionNumber,
    statusCode,
    transactionType,
    isAdjustable,
    submittedBy,
  } = transaction;
  const { t } = useTranslation();
  const [wasWarningDismissed, setWasWarningDismissed] = useState(false);
  const isAdjustment = transactionType === TransactionType.Adjustment;
  const isRetreader = transactionType === TransactionType.Retreading;
  const isDeliveryOrReceipt = transactionType === TransactionType.Delivery || transactionType === TransactionType.Receipt;
  const isPickupOrShipment = transactionType === TransactionType.Pickup || transactionType === TransactionType.Shipment;
  const showClaimNumber = isDeliveryOrReceipt;
  const showTrailerNumber = isDeliveryOrReceipt || isPickupOrShipment;
  const isEditable =
    statusCode === TransactionStatus.InReview ||
    (isAdjustable && statusCode === TransactionStatus.Submitted);
  const canOpenRequest = isPermitted({
    name: PERMISSIONS.requestDetails,
    level: PermissionLevel.Read,
  });

  useEffect(() => {
    setWasWarningDismissed(false);
  }, [transactionNumber]);

  const isTransactionLoadCorrected = useMemo(
    () =>
      !!corrections &&
      isTransactionLoadChanged(
        corrections.initialData.materials,
        corrections.correctedData.materials
      ),
    [corrections]
  );

  const isTransactionLoadAdjusted = useMemo(
    () =>
      isTransactionLoadChanged(
        transaction.materials,
        transaction.transactionType === TransactionType.Adjustment
          ? linkedOriginal?.materials
          : linkedAdjustment?.materials
      ),
    [linkedAdjustment, linkedOriginal, transaction]
  );

  const isWarningVisible =
    isTransactionLoadCorrected || isTransactionLoadAdjusted;

  const isInvalidatedDelivery =
    transactionType === TransactionType.Delivery &&
    statusCode === TransactionStatus.Invalid;

  const processorWarehouse = isInitiatedDelivery || isInvalidatedDelivery
    ? buildProcessorWarehouse(targetCompanyInfo)
    : undefined;

  const flagsBySection = useMemo(
    () =>
      Object.values(TransactionSection).reduce((result, s) => {
        result[s] = transactionFlags.filter((f) => f.sectionCode === s);
        return result;
      }, {} as { [key in TransactionSection]: TransactionFlag[] }),
    [transactionFlags]
  );

  const getFlagComments = (sectionCode: TransactionSection, subSectionCode?: TransactionSubSection) => {
    const subSectionFilteredFlags = flagsBySection[sectionCode].filter(x => x.subSectionCode === subSectionCode);
    const flags = subSectionCode ? subSectionFilteredFlags : flagsBySection[sectionCode];

    return (
      <FlagComments
        flags={flags}
        transactionType={transactionType}
        isEditable={!!canEditManualFlag && isEditable}
        sectionCode={sectionCode}
        activitiesQueryKey={activitiesQueryKey}
        transactionQueryKey={transactionQueryKey}
        transactionId={transactionId}
        transactionNumber={transactionNumber}
      />)
  };

  const getSectionAction = (
    sectionCode: TransactionSection,
    callback?: () => void
  ) => {
    if (!isEditable) return undefined;

    if (isRetreader) {
      const canEdit =
        canEditRetreadingTransaction && isCurrentUserAssignee && callback;

      return canEdit ? (
        <IconButton
          hasTooltip
          onClick={callback}
          id="edit-flag-comment-button"
          aria-label={t("transactions.actions.edit")}
        >
          <EditIcon fillColor={null} />
        </IconButton>
      ) : undefined;
    }

    return (
      <SectionAction
        transactionType={transactionType}
        sectionCode={sectionCode}
        transactionId={transactionId}
        editCallback={callback}
        activitiesQueryKey={activitiesQueryKey}
        transactionQueryKey={transactionQueryKey}
        transactionNumber={transactionNumber}
        flags={flagsBySection[sectionCode]}
        canFlagManually={canFlagManually}
        canEditManualFlag={canEditManualFlag}
        canUnflag={canUnflag}
      />
    );
  };

  return (
    <>
      <DetailsLocationsSection
        isStaffView
        transaction={transaction}
        sectionAction={getSectionAction(Locations)}
        isInitiatedDelivery={isInitiatedDelivery}
        canOpenRequest={canOpenRequest}
      >
        {getFlagComments(Locations)}
      </DetailsLocationsSection>
      {transaction.isAutoApproved && (
        <Banner id="auto-approval-info" variant="info" title={t("info")}>
          {t("transactions.details.autoApprovalInfo")}
        </Banner>
      )}
      <DetailsParticipantsSection
        isStaffView
        sourceWarehouse={sourceWarehouse}
        targetWarehouse={processorWarehouse ?? targetWarehouse}
        transactionType={transactionType}
        submittedBy={submittedBy}
        sectionAction={getSectionAction(Participants)}
      >
        {getFlagComments(Participants)}
      </DetailsParticipantsSection>
      <DetailsDataSection
        isStaffView
        transactionOrTransfer={transaction}
        sectionAction={getSectionAction(Data, dataSectionCallback)}
        materials={materials}
        displayBufferDetails
        afterDataContent={
          showTrailerNumber ? (
            <div className={styles.identifiersBlock}>
              {showClaimNumber && (
                <div id="transaction-claim-number-value" className={styles.claimNumberBlock}>
                  <span>{t("transactions.details.claimNumber.label")}</span>
                  <span>{transaction.claimNumber || t("empty")}</span>
                </div>
              )}
              <div id="transaction-trailer-number-value" className={styles.claimNumberBlock}>
                <span>{t("transactions.details.trailerNumber.label")}</span>
                <span>{transaction.trailerNumber || t("empty")}</span>
              </div>
            </div>
          ) : undefined
        }
      >
        {getFlagComments(Data, CommonData)}
        {!isAdjustment && !isRetreader && (
          <CorrectionsDetails
            isStaff
            actions={actions}
            corrections={corrections}
            transaction={transaction}
            isPermitted={isPermitted}
            isCurrentUserAssignee={isCurrentUserAssignee}
            correctionsQueryKey={correctionsQueryKey}
            activitiesQueryKey={activitiesQueryKey}
            transactionQueryKey={transactionQueryKey}
          />
        )}
        {!wasWarningDismissed && isWarningVisible && (
          <Banner
            variant="warning"
            isScrolledIntoView={false}
            title={t("transactions.warning.title")}
            id="transaction-load-changed-warning-banner"
            onDismiss={() => {
              setWasWarningDismissed(true);
            }}
          >
            {t("transactions.warning.description", {
              type: t("transactions.warning.transaction"),
              action: t(
                `transactions.warning.${isTransactionLoadAdjusted ? "adjusted" : "corrected"
                }`
              ),
            })}
          </Banner>
        )}
      </DetailsDataSection>
    </>
  );
};

export default TransactionContentView;
